import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Save, RefreshCw, ShieldCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import { apiFetch } from '../../services/api';
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-Tab: Tracking & Cookies.
 *
 * Stellt Ein-/Aus-Schalter + ID-Felder für Google Ads, GA4, Meta-Pixel,
 * LinkedIn Insight, TikTok-Pixel bereit. IDs werden im Backend gespeichert
 * und vom öffentlichen `/api/tracking/config` an Banner + TrackingLoader
 * weitergegeben. Sobald ein Dienst aktiviert wird, taucht automatisch die
 * passende DSGVO-Klausel in der Datenschutzerklärung auf.
 */

const trackerDefs = [
  {
    key: 'googleAds',
    title: 'Google Ads Conversion Tracking',
    description: 'Misst Conversions deiner Google-Ads-Kampagnen (z. B. Käufe über Digistore24).',
    fields: [
      { name: 'conversionId', label: 'Conversion ID', placeholder: 'AW-123456789' },
      { name: 'conversionLabel', label: 'Conversion Label (optional)', placeholder: 'abcDEFghi123' },
    ],
    docLink: 'https://support.google.com/google-ads/answer/6095821' },
  {
    key: 'googleAnalytics',
    title: 'Google Analytics 4',
    description: 'Anonyme Reichweitenmessung (IP-Anonymisierung ist standardmäßig aktiv).',
    fields: [
      { name: 'measurementId', label: 'Measurement ID', placeholder: 'G-XXXXXXXXXX' },
    ],
    docLink: 'https://support.google.com/analytics/answer/9539598' },
  {
    key: 'metaPixel',
    title: 'Meta-Pixel (Facebook / Instagram)',
    description: 'Tracking & Retargeting für Meta-Werbeanzeigen.',
    fields: [
      { name: 'pixelId', label: 'Pixel ID', placeholder: '123456789012345' },
    ],
    docLink: 'https://www.facebook.com/business/help/952192354843755' },
  {
    key: 'linkedInInsight',
    title: 'LinkedIn Insight Tag',
    description: 'Conversion-Messung für LinkedIn-Werbung (B2B).',
    fields: [
      { name: 'partnerId', label: 'Partner ID', placeholder: '1234567' },
    ],
    docLink: 'https://www.linkedin.com/help/lms/answer/a420537' },
  {
    key: 'tiktokPixel',
    title: 'TikTok-Pixel',
    description: 'Tracking für TikTok-Werbekampagnen.',
    fields: [
      { name: 'pixelId', label: 'Pixel ID', placeholder: 'C4XXXXXXXXXXXXXX' },
    ],
    docLink: 'https://ads.tiktok.com/help/article/get-started-pixel' },
];

const TrackingSettingsTab = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/api/tracking/admin/config`, {
        headers: {} });
      const data = await res.json();
      if (!data.success) throw new Error(data.detail || 'Fehler beim Laden');
      setConfig(data.config);
    } catch (err) {
      toast.error(err.message || 'Konnte Tracking-Config nicht laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateField = (trackerKey, fieldName, value) => {
    setConfig((c) => ({
      ...c,
      [trackerKey]: { ...(c?.[trackerKey] || {}), [fieldName]: value } }));
  };

  const setEnabled = (trackerKey, enabled) => {
    setConfig((c) => ({
      ...c,
      [trackerKey]: { ...(c?.[trackerKey] || {}), enabled } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const def of trackerDefs) {
        payload[def.key] = config?.[def.key] || { enabled: false };
      }
      const res = await apiFetch(`${API}/api/tracking/admin/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload) });
      const data = await res.json();
      if (!data.success) throw new Error(data.detail || 'Speichern fehlgeschlagen');
      setConfig(data.config);
      toast.success('Tracking-Einstellungen gespeichert', {
        description: 'Die Datenschutzerklärung wurde automatisch aktualisiert. Neue Besucher sehen jetzt den aktualisierten Cookie-Banner.' });
    } catch (err) {
      toast.error(err.message || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
        <CardContent className="py-10 text-center text-gray-400">Laden…</CardContent>
      </Card>
    );
  }

  const activeCount = trackerDefs.filter((d) => config?.[d.key]?.enabled).length;

  return (
    <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20" data-testid="tracking-settings-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-yellow-500" />
              Tracking &amp; Cookies
            </CardTitle>
            <CardDescription className="text-gray-400">
              Aktive Tools tauchen automatisch im Cookie-Banner UND in der Datenschutzerklärung auf. Consent-Version: <span className="text-yellow-400">v{config?.consentVersion ?? 1}</span>
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-yellow-500/40 text-yellow-300">
              {activeCount} aktiv
            </Badge>
            <Button variant="outline" size="sm" onClick={load} className="border-gray-700 text-gray-200 hover:bg-gray-800">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {trackerDefs.map((def) => {
          const sub = config?.[def.key] || {};
          const idField = def.fields[0];
          const hasId = !!(sub[idField.name] || '').toString().trim();
          return (
            <div
              key={def.key}
              className={`border rounded-lg p-4 transition-colors ${
                sub.enabled ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-gray-800 bg-black/30'
              }`}
              data-testid={`tracker-${def.key}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-white font-semibold">{def.title}</h3>
                    {sub.enabled ? (
                      <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 border">Aktiv</Badge>
                    ) : (
                      <Badge variant="outline" className="border-gray-700 text-gray-400">Inaktiv</Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-400 mt-1">{def.description}</p>
                  <a href={def.docLink} target="_blank" rel="noopener noreferrer" className="text-xs text-yellow-500 hover:underline mt-1 inline-block">
                    Wo finde ich die ID?
                  </a>
                </div>
                <Switch
                  checked={!!sub.enabled}
                  onCheckedChange={(v) => setEnabled(def.key, v)}
                  disabled={!hasId && !sub.enabled}
                  data-testid={`tracker-${def.key}-toggle`}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {def.fields.map((f) => (
                  <div key={f.name}>
                    <Label className="text-gray-300 text-xs">{f.label}</Label>
                    <Input
                      value={sub[f.name] || ''}
                      onChange={(e) => updateField(def.key, f.name, e.target.value)}
                      placeholder={f.placeholder}
                      className="bg-gray-800 border-gray-700 text-white font-mono text-sm mt-1"
                      data-testid={`tracker-${def.key}-${f.name}`}
                    />
                  </div>
                ))}
              </div>
              {sub.enabled && !hasId && (
                <p className="text-xs text-amber-400 flex items-center gap-1 mt-2">
                  <AlertCircle className="h-3 w-3" /> Aktivierung benötigt eine gültige ID – wird beim Speichern automatisch deaktiviert.
                </p>
              )}
            </div>
          );
        })}

        <div className="flex items-center justify-between pt-3 border-t border-gray-800">
          <p className="text-xs text-gray-500">
            Zuletzt aktualisiert: {config?.updatedAt ? new Date(config.updatedAt).toLocaleString('de-DE') : '—'}
            {config?.updatedBy && <> · von {config.updatedBy}</>}
          </p>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400"
            data-testid="tracking-save-btn"
          >
            <Save className="h-4 w-4 mr-2" /> {saving ? 'Speichern…' : 'Einstellungen speichern'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TrackingSettingsTab;
